import React, { useState, useEffect, useRef, useCallback, useMemo, useContext, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupsIcon from '@mui/icons-material/Groups';
import InsightsIcon from '@mui/icons-material/Insights';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { AuthContext } from '../../context/AuthContext';
import { admins, mapMappings, MAP_CONTROL_PUBLIC } from '../../Constants';
import { parseReplay, ParsedReplay, EntityStartEvent, GrenadeDetonateEvent, KillEvent } from '../MatchPage/Replay/replayParser';
import { worldToCanvas, mapConfigs, mapImageMappings, multiLevelMaps, filterFrameByLevel, filterWorldByLevel, buildEntityZLookup, isOnLevel } from '../MatchPage/Replay/replayRenderer';
import type { MultiLevelMapConfig } from '../../content/maps/mapData';
import { preloadIcons, PreloadedIcons, UTIL_ICON_URLS } from '../MatchPage/Replay/weaponIcons';
import { fetchReplayRound } from '../../services/ReplayService';
import { fetchUploadReplayRound } from '../../services/UserDemoService';
import { AnalysisMatch } from '../../services/AnalysisService';
import { teamWonRound } from '../MatchPage2/matchStats';
import { rosterSideForTeam, teamSideInRound } from './roundSide';
import { fetchAccount } from '../../services/AccountService';
import { buildRoundState, renderRoundUtility, renderRoundPlayers, OverlayRoundState, type LevelFilter } from './analysisRenderer';
import AnalysisReplayCanvas from './AnalysisReplayCanvas';
import AnalysisInspectPanel from './AnalysisInspectPanel';
import { parseMatchTime } from '../../utils/matchTime';
import BuySiteWinRatePanel from '../../components/BuySiteWinRatePanel/BuySiteWinRatePanel';
import PlayerImage from '../../components/PlayerImage';
import { mapPhotoMappings } from './mapPhotos';
import MapControlPanel, { type MapControlRound } from './MapControlPanel';
import styles from './AnalysisPage.module.css';

interface FilteredRound {
    gameId: string;
    matchId: string;
    roundNumber: number;
    season: number;
    // Perspective team id for this specific round. In team mode this is the selected team
    // for every round; in player mode it's the player's team in that match.
    teamId?: string;
    // Private uploads: one analysis can span several demos, so the round names the upload
    // its replay is stored under.
    uploadId?: string;
}

interface AnalysisState {
    filters: any;
    mode?: 'team' | 'player';
    searchedPlayerSteamId?: string;
    selectedMatchIds: string[];
    matches: AnalysisMatch[];
    filteredRounds: FilteredRound[];
    teamName: string;
    teamLogo: string;
    // Set when the subject is a donator's private upload. Its replay rounds live behind the
    // access-controlled /UserDemo endpoint, and the league-only stats panel doesn't apply.
    upload?: { uploadId: string; shareToken?: string };
}

interface InspectedRound {
    key: string;
    replay: ParsedReplay;
    round: FilteredRound;
    match: AnalysisMatch;
}

interface GrenadeEntry {
    roundKey: string;
    type: 'smoke' | 'flash' | 'he' | 'molotov';
    x: number;
    y: number;
    throwX?: number;
    throwY?: number;
    tick: number;
    throwerSlot: number;
    throwerName: string;
    roundNumber: number;
    matchDate: string;
    frameIdx: number;
    // Nuke panel it is drawn on (the thrower's floor when it went off); null on one-level maps
    level: Level | null;
}

type Level = 'upper' | 'lower';

// One drawn map panel of the overlay canvas. Nuke is two cropped panels side by side (upper
// left, lower right); every other map is a single full panel with level null. A point from
// worldToCanvas(..., size) is drawn at +(dx, dy) and only shows inside clip.
interface OverlayPanel {
    level: Level | null;
    dx: number;
    dy: number;
    clip: { x: number; y: number; w: number; h: number };
}

function overlayPanels(ml: MultiLevelMapConfig | undefined, size: number): OverlayPanel[] {
    if (!ml) return [{ level: null, dx: 0, dy: 0, clip: { x: 0, y: 0, w: size, h: size } }];
    const s = size / 1024;
    const uc = ml.upperCrop, lc = ml.lowerCrop;
    return [
        { level: 'upper', dx: -uc.x * s, dy: -uc.y * s, clip: { x: 0, y: 0, w: uc.w * s, h: uc.h * s } },
        { level: 'lower', dx: uc.w * s - lc.x * s, dy: -lc.y * s, clip: { x: uc.w * s, y: 0, w: lc.w * s, h: lc.h * s } },
    ];
}

function drawInPanel(ctx: CanvasRenderingContext2D, panel: OverlayPanel, draw: () => void) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(panel.clip.x, panel.clip.y, panel.clip.w, panel.clip.h);
    ctx.clip();
    ctx.translate(panel.dx, panel.dy);
    draw();
    ctx.restore();
}

// Canvas position of a worldToCanvas point inside this panel, or null where the crop hides it
function panelPoint(panel: OverlayPanel, pos: { x: number; y: number }): { x: number; y: number } | null {
    const x = pos.x + panel.dx, y = pos.y + panel.dy;
    const { clip } = panel;
    return x >= clip.x && x <= clip.x + clip.w && y >= clip.y && y <= clip.y + clip.h ? { x, y } : null;
}

type ViewMode = 'default' | 'heatmap' | 'grenades' | 'stats' | 'control';
type BuyType = 'pistol' | 'eco' | 'semi-eco' | 'semi-buy' | 'full-buy';

// Same choices as the filter page's buy rows; none selected shows every buy
const BUY_TYPES: { id: BuyType; label: string }[] = [
    { id: 'pistol', label: 'Pistol' },
    { id: 'eco', label: 'Eco' },
    { id: 'semi-eco', label: 'Semi Eco' },
    { id: 'semi-buy', label: 'Semi Buy' },
    { id: 'full-buy', label: 'Full Buy' },
];

const PLAYER_COLORS = ['#e74c3c', '#f1c40f', '#3498db', '#e91e9f', '#2ecc71',
                        '#e67e22', '#9b59b6', '#1abc9c', '#ffffff', '#95a5a6'];

const fmtClock = (sec: number) => {
    const r = Math.max(0, 115 - sec);
    return `${String(Math.floor(r / 60)).padStart(2, '0')}:${String(Math.floor(r % 60)).padStart(2, '0')}`;
};

const HeatmapControls: React.FC<{
    heatmapTimeRange: [number, number];
    setHeatmapTimeRange: (v: [number, number]) => void;
    heatmapSmoothing: number;
    setHeatmapSmoothing: (v: number) => void;
    maxTime: number;
}> = ({ heatmapTimeRange, setHeatmapTimeRange, heatmapSmoothing, setHeatmapSmoothing, maxTime }) => (
    <>
        <div className="text-secondary small mt-2">Time Range</div>
        <div className="d-flex align-items-center gap-2">
            <span className="text-light small" style={{ minWidth: 40 }}>{fmtClock(heatmapTimeRange[0])}</span>
            <span className="text-secondary small">to</span>
            <span className="text-light small" style={{ minWidth: 40 }}>{fmtClock(heatmapTimeRange[1])}</span>
        </div>
        <div className="d-flex gap-2 align-items-center">
            <input type="range" min={0} max={maxTime} step={1} value={heatmapTimeRange[0]}
                onChange={e => {
                    const v = parseFloat(e.target.value);
                    setHeatmapTimeRange([Math.min(v, heatmapTimeRange[1] - 1), heatmapTimeRange[1]]);
                }} style={{ flex: 1 }} />
            <input type="range" min={0} max={maxTime} step={1} value={heatmapTimeRange[1]}
                onChange={e => {
                    const v = parseFloat(e.target.value);
                    setHeatmapTimeRange([heatmapTimeRange[0], Math.max(v, heatmapTimeRange[0] + 1)]);
                }} style={{ flex: 1 }} />
        </div>
        <div className="text-secondary small mt-1">Smoothing</div>
        <input type="range" min={5} max={40} value={heatmapSmoothing}
            onChange={e => setHeatmapSmoothing(parseInt(e.target.value))} />
    </>
);

const AnalysisViewPage: React.FC = () => {
    const navigate = useNavigate();
    const { authLoaded, user, isBeta } = useContext(AuthContext);

    // Map control is still alpha - its tuning constants were swept by eye and per-player attribution
    // is presence rather than credit - so it stays off the public Analysis view even though the page
    // itself is public now. It used to be hidden only because the whole page was gated.
    const canSeeMapControl = MAP_CONTROL_PUBLIC || isBeta || (!!user && admins.includes(user.steamID));
    const viewModes: ViewMode[] = canSeeMapControl
        ? ['default', 'heatmap', 'grenades', 'stats', 'control']
        : ['default', 'heatmap', 'grenades', 'stats'];

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const mapImgRef = useRef<HTMLImageElement | null>(null);
    const lowerMapImgRef = useRef<HTMLImageElement | null>(null);
    // The layout the last render drew with, so mouse hit-tests land on what is on screen
    const overlayLayoutRef = useRef<{ size: number; panels: OverlayPanel[] } | null>(null);
    const animFrameRef = useRef<number>(0);

    const [viewMode, setViewMode] = useState<ViewMode>('default');
    // a round opened from the map-control tab keeps the overlay on, so "watch" lands on the same picture
    const [openedWithControl, setOpenedWithControl] = useState(false);
    const [analysisState, setAnalysisState] = useState<AnalysisState | null>(null);
    const [loadedRounds, setLoadedRounds] = useState<Map<string, ParsedReplay>>(new Map());
    const [loadProgress, setLoadProgress] = useState({ loaded: 0, total: 0 });
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [maxTime, setMaxTime] = useState(115);
    const [speed, setSpeed] = useState(1);
    const [inspectedRound, setInspectedRound] = useState<InspectedRound | null>(null);
    const [hoveredRoundKey, setHoveredRoundKey] = useState<string | null>(null);
    const mousePos = useRef<{ x: number; y: number } | null>(null);

    // Zoom/pan for inspected round replay canvas
    const [replayZoom, setReplayZoom] = useState(1);
    const [replayPanOffset, setReplayPanOffset] = useState({ x: 0, y: 0 });
    const [showTeam, setShowTeam] = useState<'team' | 'opponent'>('team');
    const [viewSide, setViewSide] = useState<'all' | 'T' | 'CT'>('T');
    const [buyFilter, setBuyFilter] = useState<Set<BuyType>>(new Set());
    const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);
    const [playerNames, setPlayerNames] = useState<Record<string, string>>({});
    const [icons, setIcons] = useState<PreloadedIcons | null>(null);
    const [roundStates, setRoundStates] = useState<Map<string, OverlayRoundState>>(new Map());

    // Heatmap state
    const [heatmapTimeRange, setHeatmapTimeRange] = useState<[number, number]>([0, 15]);
    const [heatmapSmoothing, setHeatmapSmoothing] = useState(15);

    // Grenade state
    const [grenadeFilter, setGrenadeFilter] = useState<Set<string>>(new Set(['smoke', 'flash', 'he', 'molotov']));
    const [grenadeTimeRange, setGrenadeTimeRange] = useState<[number, number]>([0, 115]);
    const [selectionBox, setSelectionBox] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    // Load analysis state
    useEffect(() => {
        const stored = sessionStorage.getItem('analysisState');
        if (!stored) { navigate('/analyse'); return; }
        try {
            const parsed: AnalysisState = JSON.parse(stored);
            setAnalysisState(parsed);
            // In player mode, open focused on the searched player so you see just their dot
            // moving through each round. Click the player in the list to toggle back to the full team.
            if (parsed.mode === 'player' && parsed.searchedPlayerSteamId) {
                setSelectedPlayer(parsed.searchedPlayerSteamId);
            }
        } catch { navigate('/analyse'); }
    }, [navigate]);

    // Preload weapon/util icons
    useEffect(() => {
        preloadIcons().then(setIcons);
    }, []);

    // Load map image (+ lower image for multi-level maps like Nuke)
    useEffect(() => {
        if (!analysisState?.matches?.length) return;
        const mapName = analysisState.matches[0].mapName;
        const mlConfig = multiLevelMaps[mapName];
        if (mlConfig) {
            const upperSrc = mapImageMappings[mlConfig.upperImage];
            const lowerSrc = mapImageMappings[mlConfig.lowerImage];
            if (upperSrc) { const img = new Image(); img.onload = () => { mapImgRef.current = img; }; img.src = upperSrc; }
            if (lowerSrc) { const img = new Image(); img.onload = () => { lowerMapImgRef.current = img; }; img.src = lowerSrc; }
        } else {
            const imgSrc = mapImageMappings[mapName];
            if (!imgSrc) return;
            const img = new Image();
            img.onload = () => { mapImgRef.current = img; };
            img.src = imgSrc;
            lowerMapImgRef.current = null;
        }
    }, [analysisState]);

    // Rounds for the map-control tab, with the side each round was actually played on.
    // Roster only: this tab's shares are per side, so a round whose lineup can't be found in the
    // demo is skipped rather than guessed (see roundSide.ts).
    const mapControlRounds = useMemo<MapControlRound[]>(() => {
        if (!analysisState || loadedRounds.size === 0) return [];
        const out: MapControlRound[] = [];
        loadedRounds.forEach((replay, key) => {
            const roundInfo = analysisState.filteredRounds.find(r => key === `${r.gameId}_r${r.roundNumber}`);
            const match = analysisState.matches.find(m => m.gameid === roundInfo?.gameId);
            if (!roundInfo || !match) return;
            const teamId = roundInfo.teamId ?? match.playerTeamId ?? analysisState.filters?.teamId;
            if (!teamId) return;
            const ourSide = rosterSideForTeam(match, teamId, replay);
            if (!ourSide) return;
            const end = match.roundEnds?.[roundInfo.roundNumber];
            out.push({
                key,
                roundNumber: roundInfo.roundNumber,
                replay,
                ourSide,
                // Uploaded demos carry no round-end team id, so fall back to the reason — and
                // here the side is roster-derived, so the fallback is exact even in overtime.
                won: end
                    ? teamWonRound(end.reason, end.teamWonID, teamId,
                        match.team1Id === teamId ? match.team2Id : match.team1Id,
                        ourSide === 'CT')
                    : null,
            });
        });
        return out.sort((a, b) => a.roundNumber - b.roundNumber);
    }, [analysisState, loadedRounds]);

    // Load all round replays
    useEffect(() => {
        if (!analysisState) return;
        const { filteredRounds } = analysisState;
        setLoadProgress({ loaded: 0, total: filteredRounds.length });

        // Fetch in parallel with a concurrency cap so 100+ round analyses don't
        // serialize on RTT. 8 saturates WebDAV without exhausting the browser's
        // per-origin HTTP/2 stream budget.
        const CONCURRENCY = 8;

        const loadAll = async () => {
            const newMap = new Map<string, ParsedReplay>();
            let completed = 0;
            let nextIdx = 0;

            const worker = async () => {
                while (true) {
                    const i = nextIdx++;
                    if (i >= filteredRounds.length) return;
                    const round = filteredRounds[i];
                    const key = `${round.gameId}_r${round.roundNumber}`;
                    try {
                        // A private upload's rounds are fetched through the owner/share-token
                        // gated endpoint; the public /Replay path is keyed differently anyway.
                        // Rounds can come from several demos at once, so each names its own
                        // upload — falling back to the state-level one for analyses saved
                        // before rounds carried it.
                        const upload = analysisState.upload;
                        const uploadId = round.uploadId ?? upload?.uploadId;
                        const data = uploadId
                            ? await fetchUploadReplayRound(uploadId, round.gameId, round.roundNumber, upload?.shareToken)
                            : await fetchReplayRound(round.season, round.matchId, round.gameId, round.roundNumber);
                        const decompressed = await new Response(
                            new Blob([data]).stream().pipeThrough(new DecompressionStream('gzip'))
                        ).arrayBuffer();
                        const parsed = parseReplay(decompressed);
                        newMap.set(key, parsed);
                    } catch (e) {
                        console.warn(`Failed to load ${key}:`, e);
                    }
                    completed++;
                    setLoadProgress({ loaded: completed, total: filteredRounds.length });
                }
            };

            const workerCount = Math.min(CONCURRENCY, filteredRounds.length);
            await Promise.all(Array.from({ length: workerCount }, worker));
            setLoadedRounds(newMap);

            // Build round states for utility tracking (smokeTeams, fireTeams)
            const states = new Map<string, OverlayRoundState>();
            newMap.forEach((replay, key) => {
                const roundInfo = filteredRounds.find(r => key === `${r.gameId}_r${r.roundNumber}`);
                const matchInfo = analysisState!.matches.find(m => m.gameid === roundInfo?.gameId);
                if (!roundInfo || !matchInfo) return;
                const perspectiveTeamId = roundInfo.teamId ?? matchInfo.playerTeamId ?? analysisState!.filters.teamId;
                const teamSide = teamSideInRound(matchInfo, perspectiveTeamId, roundInfo.roundNumber, replay);
                states.set(key, buildRoundState(replay, teamSide, key));
            });
            setRoundStates(states);

            if (newMap.size > 0) {
                let maxFrames = 0;
                newMap.forEach(replay => { if (replay.frames.length > maxFrames) maxFrames = replay.frames.length; });
                const tickRate = newMap.values().next().value?.header?.tickRate || 64;
                setMaxTime(Math.ceil(maxFrames / tickRate));
            }
        };
        loadAll();
    }, [analysisState]);

    // Determine which roster side ('T' or 'CT') the selected team is on for a given round
    const getTeamSideForRound = useCallback((roundKey: string): 'T' | 'CT' | null => {
        if (!analysisState) return null;
        const roundInfo = analysisState.filteredRounds.find(r => roundKey === `${r.gameId}_r${r.roundNumber}`);
        const matchInfo = analysisState.matches.find(m => m.gameid === roundInfo?.gameId);
        if (!roundInfo || !matchInfo) return null;

        // In player mode each round carries its own perspective team (the player's team
        // in that match). Fall back to the global filter for legacy team-mode states.
        const perspectiveTeamId = roundInfo.teamId ?? matchInfo.playerTeamId ?? analysisState.filters.teamId;
        if (!perspectiveTeamId) return null;

        return teamSideInRound(matchInfo, perspectiveTeamId, roundInfo.roundNumber, loadedRounds.get(roundKey));
    }, [analysisState, loadedRounds]);

    // Visible round keys after applying the analysis view's side filter
    // viewSide = which side the VIEWED entity (team or opponent) should be on
    const visibleRoundKeys = useMemo(() => {
        if (!analysisState) return new Set<string>();
        const keys = new Set<string>();
        for (const round of analysisState.filteredRounds) {
            const key = `${round.gameId}_r${round.roundNumber}`;
            if (!loadedRounds.has(key)) continue;

            const teamSide = getTeamSideForRound(key);

            if (viewSide !== 'all') {
                // When viewing opponent, flip: "T" means opponent is T → team is CT
                const targetSide = showTeam === 'opponent'
                    ? (viewSide === 'T' ? 'CT' : 'T')
                    : viewSide;
                if (teamSide !== targetSide) continue;
            }

            // Buy-type filter is applied to the team currently being DISPLAYED so the
            // filter matches what the user sees on screen (toggling showTeam flips it).
            if (buyFilter.size > 0) {
                if (!teamSide) continue;
                const matchInfo = analysisState.matches.find(m => m.gameid === round.gameId);
                const roundEnd = matchInfo?.roundEnds?.[round.roundNumber];
                if (!roundEnd) continue;
                const drawSide = showTeam === 'team' ? teamSide : (teamSide === 'T' ? 'CT' : 'T');
                const buyType = drawSide === 'CT' ? roundEnd.ctBuyType : roundEnd.tBuyType;
                if (!buyType || !buyFilter.has(buyType as BuyType)) continue;
            }

            keys.add(key);
        }
        return keys;
    }, [analysisState, loadedRounds, viewSide, getTeamSideForRound, showTeam, buyFilter]);

    // Build player roster from loaded rounds (consistent player → color mapping)
    const playerRoster = useMemo(() => {
        if (!analysisState) return [];
        const players: { steamId: string; name: string; colorIdx: number }[] = [];
        const seen = new Set<string>();
        const searched = analysisState.searchedPlayerSteamId;

        // First pass: check whether the searched player shows up on the currently drawn
        // side in any visible round. If they do, reserve color 0 for them so the list
        // color matches the dot the renderer draws (which also pins color 0).
        let searchedOnDrawSide = false;
        if (searched) {
            loadedRounds.forEach((replay, key) => {
                if (searchedOnDrawSide) return;
                if (!visibleRoundKeys.has(key)) return;
                const teamSide = getTeamSideForRound(key);
                if (!teamSide) return;
                const drawSide = showTeam === 'team' ? teamSide : (teamSide === 'T' ? 'CT' : 'T');
                if (replay.roster.some(r => r && r.team === drawSide && r.steamId === searched)) {
                    searchedOnDrawSide = true;
                }
            });
        }
        if (searchedOnDrawSide && searched) {
            players.push({ steamId: searched, name: searched, colorIdx: 0 });
            seen.add(searched);
        }

        loadedRounds.forEach((replay, key) => {
            if (!visibleRoundKeys.has(key)) return;
            const teamSide = getTeamSideForRound(key);
            if (!teamSide) return;
            const drawSide = showTeam === 'team' ? teamSide : (teamSide === 'T' ? 'CT' : 'T');

            for (let i = 0; i < replay.roster.length; i++) {
                const r = replay.roster[i];
                if (!r || r.team !== drawSide) continue;
                if (!seen.has(r.steamId)) {
                    seen.add(r.steamId);
                    players.push({ steamId: r.steamId, name: r.steamId, colorIdx: players.length });
                }
            }
        });
        return players;
    }, [loadedRounds, visibleRoundKeys, showTeam, getTeamSideForRound, analysisState]);

    // Precompute heatmap image (only recomputes when inputs change, NOT on every currentTime)
    // Multi-level maps (Nuke) return { upper, lower } so each panel can be drawn with its own
    // crop; single-level maps return { single }.
    // Deferred slider values let the thumb stay responsive: the urgent state drives the input,
    // the deferred copy drives the (expensive) rebuild, and React drops stale intermediates.
    const deferredTimeRange = useDeferredValue(heatmapTimeRange);
    const deferredSmoothing = useDeferredValue(heatmapSmoothing);
    const heatmapImage = useMemo<
        | { kind: 'single'; canvas: HTMLCanvasElement }
        | { kind: 'multi'; upper: HTMLCanvasElement | null; lower: HTMLCanvasElement | null }
        | null
    >(() => {
        if (viewMode !== 'heatmap' || !analysisState || loadedRounds.size === 0) return null;
        const mapName = analysisState.matches[0]?.mapName;
        const config = mapConfigs[mapName];
        if (!config) return null;
        const mlCfg = multiLevelMaps[mapName];

        const GRID = 256;
        const refSize = 700; // reference size for coordinate mapping
        const cellSize = refSize / GRID;

        // Splat once per position, then convolve with a separable Gaussian. Equivalent to
        // splatting a 2D Gaussian per position but O(positions + GRID²·kRadius) instead of
        // O(positions · kRadius²).
        const sigma = Math.max(0.8, deferredSmoothing / cellSize / 2);
        const kRadius = Math.max(1, Math.ceil(sigma * 3));
        const kSize = kRadius * 2 + 1;
        const kernel = new Float32Array(kSize);
        {
            const inv2s2 = 1 / (2 * sigma * sigma);
            let kSum = 0;
            for (let i = -kRadius; i <= kRadius; i++) {
                const v = Math.exp(-(i * i) * inv2s2);
                kernel[i + kRadius] = v;
                kSum += v;
            }
            for (let i = 0; i < kSize; i++) kernel[i] /= kSum;
        }
        const scratch = new Float32Array(GRID * GRID);

        const buildCanvas = (zFilter?: (z: number, x: number, y: number) => boolean): HTMLCanvasElement | null => {
            const density = new Float32Array(GRID * GRID);

            loadedRounds.forEach((replay, key) => {
                if (!visibleRoundKeys.has(key)) return;
                const teamSide = getTeamSideForRound(key);
                if (!teamSide) return;
                const drawSide = showTeam === 'team' ? teamSide : (teamSide === 'T' ? 'CT' : 'T');
                const tickRate = replay.header?.tickRate || 64;
                const startTick = Math.floor(deferredTimeRange[0] * tickRate);
                const endTick = Math.floor(deferredTimeRange[1] * tickRate);

                for (let fi = 0; fi < replay.frames.length; fi += 4) {
                    const frame = replay.frames[fi];
                    if (frame.tick < startTick || frame.tick > endTick) continue;
                    for (let i = 0; i < replay.roster.length; i++) {
                        const entry = replay.roster[i];
                        if (!entry || entry.team !== drawSide) continue;
                        if (selectedPlayer && entry.steamId !== selectedPlayer) continue;
                        const player = frame.players?.[i];
                        if (!player || !player.isAlive) continue;
                        if (zFilter && !zFilter(player.z, player.x, player.y)) continue;
                        const pos = worldToCanvas(player.x, player.y, config, refSize);
                        const gx = Math.floor(pos.x / cellSize);
                        const gy = Math.floor(pos.y / cellSize);
                        if (gx < 0 || gx >= GRID || gy < 0 || gy >= GRID) continue;
                        density[gy * GRID + gx] += 1;
                    }
                }
            });

            // Separable Gaussian: horizontal pass density → scratch, vertical pass scratch → density.
            // Reset scratch since the same buffer is reused across upper/lower builds for Nuke.
            scratch.fill(0);
            for (let y = 0; y < GRID; y++) {
                const rowOff = y * GRID;
                for (let x = 0; x < GRID; x++) {
                    let acc = 0;
                    const kMin = x < kRadius ? -x : -kRadius;
                    const kMax = x + kRadius >= GRID ? GRID - 1 - x : kRadius;
                    for (let k = kMin; k <= kMax; k++) {
                        acc += density[rowOff + x + k] * kernel[k + kRadius];
                    }
                    scratch[rowOff + x] = acc;
                }
            }
            for (let x = 0; x < GRID; x++) {
                for (let y = 0; y < GRID; y++) {
                    let acc = 0;
                    const kMin = y < kRadius ? -y : -kRadius;
                    const kMax = y + kRadius >= GRID ? GRID - 1 - y : kRadius;
                    for (let k = kMin; k <= kMax; k++) {
                        acc += scratch[(y + k) * GRID + x] * kernel[k + kRadius];
                    }
                    density[y * GRID + x] = acc;
                }
            }

            let maxD = 0;
            for (let i = 0; i < density.length; i++) if (density[i] > maxD) maxD = density[i];
            if (maxD === 0) return null;

            const canvas = document.createElement('canvas');
            canvas.width = GRID;
            canvas.height = GRID;
            const offCtx = canvas.getContext('2d')!;
            const imgData = offCtx.createImageData(GRID, GRID);

            for (let i = 0; i < density.length; i++) {
                const v = density[i] / maxD;
                const px = i * 4;
                if (v < 0.01) { imgData.data[px + 3] = 0; continue; }
                if (v < 0.15) {
                    const t = v / 0.15;
                    imgData.data[px] = Math.floor(60 * t); imgData.data[px + 1] = 0;
                    imgData.data[px + 2] = Math.floor(100 * t); imgData.data[px + 3] = Math.floor(80 * t);
                } else if (v < 0.35) {
                    const t = (v - 0.15) / 0.2;
                    imgData.data[px] = 60 + Math.floor(195 * t); imgData.data[px + 1] = 0;
                    imgData.data[px + 2] = Math.floor(100 * (1 - t)); imgData.data[px + 3] = 80 + Math.floor(100 * t);
                } else if (v < 0.6) {
                    const t = (v - 0.35) / 0.25;
                    imgData.data[px] = 255; imgData.data[px + 1] = Math.floor(120 * t);
                    imgData.data[px + 2] = 0; imgData.data[px + 3] = 180 + Math.floor(40 * t);
                } else {
                    const t = (v - 0.6) / 0.4;
                    imgData.data[px] = 255; imgData.data[px + 1] = 120 + Math.floor(135 * t);
                    imgData.data[px + 2] = Math.floor(80 * t); imgData.data[px + 3] = 220 + Math.floor(35 * t);
                }
            }
            offCtx.putImageData(imgData, 0, 0);
            return canvas;
        };

        if (mlCfg) {
            const { zUpper, zLower, bothZone } = mlCfg;
            const midZ = (zUpper + zLower) / 2;
            const inBothZone = (x: number, y: number) =>
                !!bothZone && x >= bothZone.minX && x <= bothZone.maxX &&
                y >= bothZone.minY && y <= bothZone.maxY;
            const isUpper = (z: number, x: number, y: number) => {
                if (z >= zUpper) return true;
                if (z <= zLower) return false;
                if (inBothZone(x, y)) return true;
                return z >= midZ;
            };
            const isLower = (z: number, x: number, y: number) => {
                if (z <= zLower) return true;
                if (z >= zUpper) return false;
                if (inBothZone(x, y)) return true;
                return z < midZ;
            };
            return { kind: 'multi', upper: buildCanvas(isUpper), lower: buildCanvas(isLower) };
        }

        const canvas = buildCanvas();
        return canvas ? { kind: 'single', canvas } : null;
    }, [viewMode, loadedRounds, visibleRoundKeys, deferredTimeRange, deferredSmoothing,
        selectedPlayer, showTeam, getTeamSideForRound, analysisState]);

    // Resolve player names from steamIds
    useEffect(() => {
        const unknownIds = playerRoster.filter(p => !playerNames[p.steamId]).map(p => p.steamId);
        if (unknownIds.length === 0) return;
        const resolve = async () => {
            const newNames: Record<string, string> = {};
            for (const id of unknownIds) {
                try {
                    const account = await fetchAccount(id);
                    if (account?.playerName) newNames[id] = account.playerName;
                } catch { /* skip */ }
            }
            if (Object.keys(newNames).length > 0) {
                setPlayerNames(prev => ({ ...prev, ...newNames }));
            }
        };
        resolve();
    }, [playerRoster]);

    // hideAfterEnd: when true, returns -1 if currentTime is past the round's last
    // recorded tick — used by the multi-round overlay so alive players don't get
    // drawn frozen at their final position after their round actually ended. When
    // inspecting a single round we leave it false to preserve the clamp-to-last
    // behavior (so the scrubber can still show the final state of that round).
    const getFrameIndexForTime = useCallback((replay: ParsedReplay, timeSec: number, hideAfterEnd = false): number => {
        const tickRate = replay.header?.tickRate || 64;
        const targetTick = timeSec * tickRate;
        const lastTick = replay.frames[replay.frames.length - 1]?.tick ?? 0;
        if (hideAfterEnd && targetTick > lastTick) return -1;
        let best = 0;
        for (let i = 0; i < replay.frames.length; i++) {
            if (replay.frames[i].tick <= targetTick) best = i;
            else break;
        }
        return best;
    }, []);

    // Animation loop
    useEffect(() => {
        if (!isPlaying) return;
        let lastTime = performance.now();
        const animate = (now: number) => {
            const delta = (now - lastTime) / 1000;
            lastTime = now;
            setCurrentTime(prev => {
                const next = prev + delta * speed;
                if (next >= maxTime) { setIsPlaying(false); return maxTime; }
                return next;
            });
            animFrameRef.current = requestAnimationFrame(animate);
        };
        animFrameRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animFrameRef.current);
    }, [isPlaying, speed, maxTime]);

    // The round whose drawn player dot is nearest the canvas point, within maxDist pixels.
    // Only dots the overlay actually draws count - rounds hidden by the side/buy filters, the
    // other team, players filtered out - so hovering and clicking match what is on screen.
    const roundAtPoint = useCallback((cx: number, cy: number, maxDist: number): string | null => {
        const layout = overlayLayoutRef.current;
        if (!analysisState || !layout) return null;
        const mapName = analysisState.matches[0]?.mapName;
        const config = mapConfigs[mapName];
        if (!config) return null;
        const ml = multiLevelMaps[mapName];

        let closestDist = maxDist;
        let closestKey: string | null = null;
        loadedRounds.forEach((replay, key) => {
            if (!visibleRoundKeys.has(key)) return;
            const frameIdx = getFrameIndexForTime(replay, currentTime, true);
            if (frameIdx < 0) return;
            const frame = replay.frames[frameIdx];
            if (!frame) return;
            const teamSide = getTeamSideForRound(key);
            if (!teamSide) return;
            const drawSide = showTeam === 'team' ? teamSide : (teamSide === 'T' ? 'CT' : 'T');

            for (let i = 0; i < replay.roster.length; i++) {
                const entry = replay.roster[i];
                if (!entry || entry.team !== drawSide) continue;
                if (selectedPlayer && entry.steamId !== selectedPlayer) continue;
                const player = frame.players?.[i];
                if (!player || !player.isAlive) continue;
                const pos = worldToCanvas(player.x, player.y, config, layout.size);
                for (const panel of layout.panels) {
                    if (panel.level && ml && !isOnLevel(player.z, panel.level, ml.zUpper, ml.zLower, player.x, player.y, ml.bothZone)) continue;
                    const p = panelPoint(panel, pos);
                    if (!p) continue;
                    const dist = Math.hypot(cx - p.x, cy - p.y);
                    if (dist < closestDist) {
                        closestDist = dist;
                        closestKey = key;
                    }
                }
            }
        });
        return closestKey;
    }, [analysisState, loadedRounds, visibleRoundKeys, currentTime, getFrameIndexForTime,
        getTeamSideForRound, showTeam, selectedPlayer]);

    // Click handler for canvas - detect player clicks
    const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!canvasRef.current || !analysisState || viewMode !== 'default') return;
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const clickX = (e.clientX - rect.left) * (canvas.width / rect.width);
        const clickY = (e.clientY - rect.top) * (canvas.height / rect.height);

        const key = roundAtPoint(clickX, clickY, 20);
        const replay = key ? loadedRounds.get(key) : undefined;
        const roundInfo = key ? analysisState.filteredRounds.find(r => key === `${r.gameId}_r${r.roundNumber}`) : undefined;
        const matchInfo = roundInfo ? analysisState.matches.find(m => m.gameid === roundInfo.gameId) : undefined;
        setInspectedRound(key && replay && roundInfo && matchInfo
            ? { key, replay, round: roundInfo, match: matchInfo }
            : null);
    }, [analysisState, loadedRounds, viewMode, roundAtPoint]);

    // Grenade mouse handlers for drag selection
    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (viewMode !== 'grenades') return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) * (canvas.width / rect.width);
        const y = (e.clientY - rect.top) * (canvas.height / rect.height);
        setSelectionBox({ x1: x, y1: y, x2: x, y2: y });
        setIsDragging(true);
    }, [viewMode]);

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) * (canvas.width / rect.width);
        const y = (e.clientY - rect.top) * (canvas.height / rect.height);

        // Grenade drag selection
        if (isDragging && viewMode === 'grenades') {
            setSelectionBox(prev => prev ? { ...prev, x2: x, y2: y } : null);
            return;
        }

        // Default mode: detect hover over player dots
        if (viewMode === 'default' && !inspectedRound && analysisState) {
            mousePos.current = { x, y };
            const foundKey = roundAtPoint(x, y, 10);
            if (foundKey !== hoveredRoundKey) {
                setHoveredRoundKey(foundKey);
                canvas.style.cursor = foundKey ? 'pointer' : 'default';
            }
        }
    }, [isDragging, viewMode, inspectedRound, analysisState, roundAtPoint, hoveredRoundKey]);

    const handleMouseUp = useCallback(() => {
        if (!isDragging || viewMode !== 'grenades') return;
        setIsDragging(false);
        // Selection will be applied by the render effect
    }, [isDragging, viewMode]);

    // Collect all grenades from loaded rounds - filtered by team side + time range
    const allGrenades = React.useMemo(() => {
        const grenades: GrenadeEntry[] = [];
        if (!analysisState) return grenades;
        const ml = multiLevelMaps[analysisState.matches[0]?.mapName];

        loadedRounds.forEach((replay, key) => {
            if (!visibleRoundKeys.has(key)) return;
            const roundInfo = analysisState.filteredRounds.find(r => key === `${r.gameId}_r${r.roundNumber}`);
            const matchInfo = analysisState.matches.find(m => m.gameid === roundInfo?.gameId);
            if (!roundInfo || !matchInfo) return;

            // Determine which side we're drawing
            const teamSide = getTeamSideForRound(key);
            if (!teamSide) return;
            const drawSide = showTeam === 'team' ? teamSide : (teamSide === 'T' ? 'CT' : 'T');

            const tickRate = replay.header?.tickRate || 64;
            const startTick = replay.frames[0]?.tick || 0;

            for (const evt of replay.events) {
                let grenadeType: GrenadeEntry['type'] | null = null;
                let x = 0, y = 0, throwerSlot = -1, frameIdx = 0;

                if (evt.type === 'smoke_start') {
                    const e = evt as EntityStartEvent;
                    grenadeType = 'smoke'; x = e.x; y = e.y; throwerSlot = e.throwerSlot; frameIdx = e.frameIdx;
                } else if (evt.type === 'flash_detonate') {
                    const e = evt as GrenadeDetonateEvent;
                    grenadeType = 'flash'; x = e.x; y = e.y; throwerSlot = e.throwerSlot; frameIdx = e.frameIdx;
                } else if (evt.type === 'he_detonate') {
                    const e = evt as GrenadeDetonateEvent;
                    grenadeType = 'he'; x = e.x; y = e.y; throwerSlot = e.throwerSlot; frameIdx = e.frameIdx;
                } else if (evt.type === 'inferno_start') {
                    const e = evt as EntityStartEvent;
                    grenadeType = 'molotov'; x = e.x; y = e.y; throwerSlot = e.throwerSlot; frameIdx = e.frameIdx;
                }

                if (!grenadeType || !grenadeFilter.has(grenadeType)) continue;

                // Filter by team side - only show nades thrown by the viewed team
                if (throwerSlot < 0 || throwerSlot >= replay.roster.length || replay.roster[throwerSlot]?.team !== drawSide) continue;

                // Filter by selected player
                if (selectedPlayer && replay.roster[throwerSlot]?.steamId !== selectedPlayer) continue;

                // Filter by time range
                const evtFrame = replay.frames[Math.min(frameIdx, replay.frames.length - 1)];
                if (evtFrame) {
                    const elapsed = (evtFrame.tick - startTick) / tickRate;
                    if (elapsed < grenadeTimeRange[0] || elapsed > grenadeTimeRange[1]) continue;
                }

                const throwerName = replay.roster[throwerSlot]?.steamId || `Player ${throwerSlot}`;
                let throwX: number | undefined, throwY: number | undefined, throwZ: number | undefined;
                if (throwerSlot >= 0 && frameIdx < replay.frames.length) {
                    const throwFrame = replay.frames[Math.min(frameIdx, replay.frames.length - 1)];
                    const thrower = throwFrame?.players?.[throwerSlot];
                    if (thrower) { throwX = thrower.x; throwY = thrower.y; throwZ = thrower.z; }
                }
                const level: Level | null = !ml ? null
                    : throwZ !== undefined && throwZ < (ml.zUpper + ml.zLower) / 2 ? 'lower' : 'upper';
                grenades.push({
                    roundKey: key,
                    type: grenadeType,
                    x, y,
                    throwX, throwY,
                    tick: frameIdx,
                    throwerSlot,
                    throwerName,
                    frameIdx,
                    roundNumber: roundInfo.roundNumber,
                    matchDate: parseMatchTime(matchInfo.matchTime).toLocaleDateString(),
                    level,
                });
            }
        });
        return grenades;
    }, [loadedRounds, analysisState, grenadeFilter, visibleRoundKeys, getTeamSideForRound, showTeam, selectedPlayer, grenadeTimeRange]);

    // Grenades inside the selection box (computed, not state)
    const selectedGrenades = useMemo(() => {
        const layout = overlayLayoutRef.current;
        if (!selectionBox || isDragging || !analysisState || !layout) return [];
        const mapName = analysisState.matches[0]?.mapName;
        const config = mapConfigs[mapName];
        if (!config) return [];
        const bx = Math.min(selectionBox.x1, selectionBox.x2);
        const by = Math.min(selectionBox.y1, selectionBox.y2);
        const bw = Math.abs(selectionBox.x2 - selectionBox.x1);
        const bh = Math.abs(selectionBox.y2 - selectionBox.y1);
        if (bw < 5 || bh < 5) return [];
        return allGrenades.filter(nade => {
            const pos = worldToCanvas(nade.x, nade.y, config, layout.size);
            return layout.panels.some(panel => {
                if (panel.level && nade.level !== panel.level) return false;
                const p = panelPoint(panel, pos);
                return !!p && p.x >= bx && p.x <= bx + bw && p.y >= by && p.y <= by + bh;
            });
        });
    }, [selectionBox, isDragging, allGrenades, analysisState]);

    // Render
    useEffect(() => {
        if (!canvasRef.current || !mapImgRef.current || !analysisState) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const mapName = analysisState.matches[0]?.mapName;
        const config = mapConfigs[mapName];
        if (!config) return;

        const mlCfg = multiLevelMaps[mapName];
        const isML = !!mlCfg && !!lowerMapImgRef.current;

        const parentW = canvas.parentElement?.clientWidth || 700;
        const parentH = canvas.parentElement?.clientHeight || 700;

        let size: number; // the "1024-equivalent" scale for coordinate mapping
        if (isML) {
            const uc = mlCfg!.upperCrop;
            const lc = mlCfg!.lowerCrop;
            const gap = 0;
            // Fit both panels + gap into the parent width
            const totalRatioW = uc.w + gap * (1024 / Math.min(parentW, parentH - 50)) + lc.w;
            const maxH = parentH - 50;
            size = Math.min(parentW * (1024 / totalRatioW), maxH);
            const s = size / 1024;
            canvas.width = Math.round(uc.w * s + gap + lc.w * s);
            canvas.height = Math.round(Math.max(uc.h, lc.h) * s);
        } else {
            size = Math.min(parentW, parentH - 50);
            canvas.width = size;
            canvas.height = size;
        }

        // Every layer below draws through these panels, and the mouse handlers read them back,
        // so on Nuke a dot, a nade and its hit area all sit on the same cropped panel.
        const panels = overlayPanels(isML ? mlCfg : undefined, size);
        overlayLayoutRef.current = { size, panels };

        // Clear canvas
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        for (const panel of panels) {
            const img = panel.level === 'lower' ? lowerMapImgRef.current! : mapImgRef.current;
            drawInPanel(ctx, panel, () => ctx.drawImage(img, 0, 0, size, size));
        }
        if (isML) {
            // Level labels
            ctx.font = `bold ${Math.max(9, Math.round(size / 90))}px Arial`;
            ctx.fillStyle = 'rgba(255,255,255,0.5)';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            for (const panel of panels) {
                ctx.fillText(panel.level === 'upper' ? 'UPPER' : 'LOWER', panel.clip.x + panel.clip.w / 2, 4);
            }
        }

        if (viewMode === 'default') {
            // --- DEFAULT: Multi-round overlay ---
            if (!inspectedRound) {
                const renderOpts = {
                    showTeam,
                    selectedPlayer,
                    playerColors: PLAYER_COLORS,
                    preloadedIcons: icons,
                    pinnedPlayerSteamId: analysisState.searchedPlayerSteamId ?? null,
                };

                loadedRounds.forEach((replay, key) => {
                    if (!visibleRoundKeys.has(key)) return;
                    const frameIdx = getFrameIndexForTime(replay, currentTime, true);
                    if (frameIdx < 0) return;
                    const frame = replay.frames[frameIdx];
                    if (!frame) return;

                    const teamSide = getTeamSideForRound(key);
                    if (!teamSide) return;
                    const drawSide = showTeam === 'team' ? teamSide : (teamSide === 'T' ? 'CT' : 'T');
                    const rs = roundStates.get(key);

                    const entityZMap = isML ? buildEntityZLookup(replay.events, replay.frames) : null;

                    for (const panel of panels) {
                        let levelFrame = frame;
                        let levelFilter: LevelFilter | undefined;
                        if (panel.level && entityZMap) {
                            const { zUpper: zU, zLower: zL } = mlCfg;
                            const filteredFrame = filterFrameByLevel(frame, panel.level, zU, zL, mlCfg.bothZone);
                            const filteredWorld = filterWorldByLevel(filteredFrame.world, entityZMap, frame, frameIdx, panel.level, zU, zL);
                            levelFrame = { ...filteredFrame, world: filteredWorld };
                            levelFilter = { level: panel.level, zUpper: zU, zLower: zL };
                        }
                        drawInPanel(ctx, panel, () => {
                            if (rs) {
                                renderRoundUtility(ctx, levelFrame, rs, mapName, size, frameIdx, renderOpts, drawSide, levelFilter);
                            }
                            renderRoundPlayers(ctx, levelFrame, replay.roster, drawSide, mapName, size, renderOpts, key === hoveredRoundKey, playerNames);
                        });
                    }
                });
            }
        } else if (viewMode === 'heatmap') {
            // --- HEATMAP: draw precomputed density image ---
            if (heatmapImage) {
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                for (const panel of panels) {
                    const img = heatmapImage.kind === 'single'
                        ? heatmapImage.canvas
                        : panel.level ? heatmapImage[panel.level] : null;
                    if (img) drawInPanel(ctx, panel, () => ctx.drawImage(img, 0, 0, size, size));
                }
            }
        } else if (viewMode === 'grenades') {
            // --- GRENADES with trajectories ---
            for (const panel of panels) drawInPanel(ctx, panel, () => {
                for (const nade of allGrenades) {
                    if (panel.level && nade.level !== panel.level) continue;
                    const landPos = worldToCanvas(nade.x, nade.y, config, size);
                    const isSelected = selectedGrenades.includes(nade);

                    // Trajectory line from throw position to landing
                    if (nade.throwX != null && nade.throwY != null) {
                        const throwPos = worldToCanvas(nade.throwX, nade.throwY, config, size);
                        ctx.beginPath();
                        ctx.setLineDash([5, 4]);
                        ctx.moveTo(throwPos.x, throwPos.y);
                        ctx.lineTo(landPos.x, landPos.y);
                        ctx.strokeStyle = isSelected ? 'rgba(255,255,255,0.8)' : 'rgba(200,200,200,0.3)';
                        ctx.lineWidth = isSelected ? 1.5 : 1;
                        ctx.stroke();
                        ctx.setLineDash([]);

                        // Throw origin circle
                        ctx.beginPath();
                        ctx.arc(throwPos.x, throwPos.y, 3, 0, Math.PI * 2);
                        ctx.strokeStyle = isSelected ? 'rgba(255,255,255,0.7)' : 'rgba(200,200,200,0.3)';
                        ctx.lineWidth = 1;
                        ctx.stroke();
                    }

                    // Landing X marker
                    ctx.beginPath();
                    ctx.moveTo(landPos.x - 4, landPos.y - 4);
                    ctx.lineTo(landPos.x + 4, landPos.y + 4);
                    ctx.moveTo(landPos.x + 4, landPos.y - 4);
                    ctx.lineTo(landPos.x - 4, landPos.y + 4);
                    ctx.strokeStyle = isSelected ? '#fff' : 'rgba(200,200,200,0.5)';
                    ctx.lineWidth = 1.5;
                    ctx.stroke();
                }
            });

            // Draw selection box
            if (selectionBox) {
                const bx = Math.min(selectionBox.x1, selectionBox.x2);
                const by = Math.min(selectionBox.y1, selectionBox.y2);
                const bw = Math.abs(selectionBox.x2 - selectionBox.x1);
                const bh = Math.abs(selectionBox.y2 - selectionBox.y1);

                ctx.strokeStyle = '#2ecc71';
                ctx.lineWidth = 2;
                ctx.setLineDash([6, 4]);
                ctx.strokeRect(bx, by, bw, bh);
                ctx.setLineDash([]);
                ctx.fillStyle = 'rgba(46, 204, 113, 0.1)';
                ctx.fillRect(bx, by, bw, bh);

                // Highlight selected grenades from useMemo
                if (!isDragging && bw > 5 && bh > 5) {
                    for (const panel of panels) drawInPanel(ctx, panel, () => {
                        for (const nade of selectedGrenades) {
                            if (panel.level && nade.level !== panel.level) continue;
                            const pos = worldToCanvas(nade.x, nade.y, config, size);
                            ctx.beginPath();
                            ctx.arc(pos.x, pos.y, 6, 0, Math.PI * 2);
                            ctx.strokeStyle = '#2ecc71';
                            ctx.lineWidth = 2;
                            ctx.stroke();
                        }
                    });
                }
            }
        }
    }, [currentTime, loadedRounds, analysisState, viewMode, getFrameIndexForTime, inspectedRound,
        allGrenades, selectionBox, isDragging, selectedGrenades,
        showTeam, getTeamSideForRound, visibleRoundKeys, selectedPlayer, icons, roundStates, heatmapImage,
        hoveredRoundKey, playerNames]);

    // Opponent name for the team/opponent toggle (single opponent → name, otherwise "Opponent")
    const opponentLabel = useMemo<string>(() => {
        if (!analysisState) return 'Opponent';
        const matches = analysisState.matches;
        const mode = analysisState.mode ?? 'team';
        const oppNames = new Set(matches.map(m => {
            const perspectiveTeamId = mode === 'player'
                ? (m.playerTeamId ?? '')
                : analysisState.filters.teamId;
            return m.team1Id === perspectiveTeamId ? m.team2Name : m.team1Name;
        }));
        return oppNames.size === 1 ? Array.from(oppNames)[0] : 'Opponent';
    }, [analysisState]);

    // Round summary stats — shown in the right panel when no round is being inspected.
    const summaryStats = useMemo(() => {
        if (!analysisState) return null;
        let tRounds = 0, ctRounds = 0;
        let wins = 0, losses = 0;
        let smokeCount = 0, flashCount = 0, heCount = 0, molyCount = 0;
        let killCount = 0;
        const matchIds = new Set<string>();

        for (const round of analysisState.filteredRounds) {
            const key = `${round.gameId}_r${round.roundNumber}`;
            if (!visibleRoundKeys.has(key)) continue;
            matchIds.add(round.gameId);

            const matchInfo = analysisState.matches.find(m => m.gameid === round.gameId);
            const replay = loadedRounds.get(key);
            const perspectiveTeamId = round.teamId ?? matchInfo?.playerTeamId ?? analysisState.filters.teamId;
            if (!matchInfo || !replay || !perspectiveTeamId) continue;

            const teamSide = getTeamSideForRound(key);
            if (!teamSide) continue;
            if (teamSide === 'T') tRounds++;
            else ctRounds++;

            const roundEnd = matchInfo.roundEnds?.[round.roundNumber];
            if (roundEnd) {
                // teamWonID can be empty or stale on uploaded demos — teamWonRound falls back
                // to which side won in both cases.
                const isTeam1 = matchInfo.team1Id === perspectiveTeamId;
                const won = teamWonRound(roundEnd.reason, roundEnd.teamWonID, perspectiveTeamId,
                    isTeam1 ? matchInfo.team2Id : matchInfo.team1Id,
                    teamSide === 'CT');
                if (won) wins++;
                else losses++;
            }

            const drawSide = showTeam === 'team' ? teamSide : (teamSide === 'T' ? 'CT' : 'T');
            for (const evt of replay.events) {
                if (evt.type === 'kill') {
                    const slot = (evt as KillEvent).killerSlot;
                    if (slot >= 0 && slot < replay.roster.length && replay.roster[slot]?.team === drawSide) {
                        if (selectedPlayer && replay.roster[slot]?.steamId !== selectedPlayer) continue;
                        killCount++;
                    }
                    continue;
                }
                const slot = (evt as { throwerSlot?: number }).throwerSlot;
                if (slot == null || slot < 0 || slot >= replay.roster.length) continue;
                if (replay.roster[slot]?.team !== drawSide) continue;
                if (selectedPlayer && replay.roster[slot]?.steamId !== selectedPlayer) continue;
                if (evt.type === 'smoke_start') smokeCount++;
                else if (evt.type === 'flash_detonate') flashCount++;
                else if (evt.type === 'he_detonate') heCount++;
                else if (evt.type === 'inferno_start') molyCount++;
            }
        }

        return {
            rounds: visibleRoundKeys.size,
            matches: matchIds.size,
            tRounds, ctRounds,
            wins, losses,
            smokes: smokeCount, flashes: flashCount, hes: heCount, molys: molyCount,
            kills: killCount,
        };
    }, [analysisState, loadedRounds, visibleRoundKeys, getTeamSideForRound, showTeam, selectedPlayer]);

    if (!analysisState) return <div className="text-light p-4">Loading...</div>;

    const progressPct = loadProgress.total > 0 ? Math.round((loadProgress.loaded / loadProgress.total) * 100) : 0;
    const roundTimeSec = 115 - currentTime;
    const clockTime = `${String(Math.floor(Math.max(0, roundTimeSec) / 60)).padStart(2, '0')}:${String(Math.floor(Math.max(0, roundTimeSec) % 60)).padStart(2, '0')}`;

    // Frame index for inspected round replay
    const inspectedFrameIdx = inspectedRound
        ? getFrameIndexForTime(inspectedRound.replay, currentTime)
        : 0;

    if (!authLoaded) return <div className="text-secondary text-center py-5">Loading...</div>;

    const mapKey = analysisState.matches[0]?.mapName ?? '';
    const mapLabel = mapMappings.get(mapKey) ?? mapKey;
    const mapThumb = mapPhotoMappings[mapKey];
    const subjectMode = analysisState.mode ?? 'team';
    // An upload's season is the 9999 sentinel — say what it actually is instead of "S9999".
    const subjectSeasonsLabel = analysisState.upload
        ? 'Private demo'
        : analysisState.filters?.seasons?.length
            ? [...analysisState.filters.seasons].sort((a: number, b: number) => b - a).map((s: number) => `S${s}`).join(' · ')
            : '';
    const subjectLogo = subjectMode === 'team' ? analysisState.teamLogo : null;
    const subjectSteamId = subjectMode === 'player' ? analysisState.searchedPlayerSteamId : null;

    const utilIcons: Record<string, string> = {
        smoke: UTIL_ICON_URLS.smoke,
        flash: UTIL_ICON_URLS.flash,
        he: UTIL_ICON_URLS.he,
        molotov: UTIL_ICON_URLS.fire,
    };

    return (
        <div className={styles.viewPage}>
            {/* Top header strip */}
            <div className={styles.viewHeader}>
                <button
                    className={styles.viewHeaderBack}
                    onClick={() => navigate('/analyse')}
                    title="Back to filter"
                >
                    <ArrowBackIcon />
                    Filter
                </button>

                <div className={styles.viewHeaderDivider} />

                {analysisState.teamName && (
                    <div className={styles.subjectPreview}>
                        <div className={styles.subjectPreviewLogo}>
                            {subjectMode === 'team'
                                ? (subjectLogo
                                    ? <img src={subjectLogo} alt="" />
                                    : <GroupsIcon style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16 }} />)
                                : subjectSteamId
                                    ? <PlayerImage id={subjectSteamId} width={28} />
                                    : <GroupsIcon style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16 }} />}
                        </div>
                        <div className={styles.subjectPreviewText}>
                            <span className={styles.subjectPreviewName}>{analysisState.teamName}</span>
                            {subjectSeasonsLabel && (
                                <span className={styles.subjectPreviewMeta}>{subjectSeasonsLabel}</span>
                            )}
                        </div>
                    </div>
                )}

                {mapKey && (
                    <div className={styles.viewHeaderMap}>
                        {mapThumb && <img src={mapThumb} alt="" className={styles.viewHeaderMapThumb} />}
                        <span className={styles.viewHeaderMapName}>{mapLabel}</span>
                    </div>
                )}

                <div className={styles.viewHeaderSpacer} />

                <div className={styles.viewModeTabs}>
                    {viewModes.map(mode => (
                        <button
                            key={mode}
                            className={`${styles.viewModeTab} ${viewMode === mode ? styles.viewModeTabActive : ''}`}
                            onClick={() => {
                                setViewMode(mode);
                                setInspectedRound(null);
                                setSelectionBox(null);
                                if (mode === 'heatmap' || mode === 'stats' || mode === 'control') setIsPlaying(false);
                            }}
                        >
                            {mode === 'default' ? 'Default'
                                : mode === 'heatmap' ? 'Heatmap'
                                : mode === 'grenades' ? 'Grenades'
                                : mode === 'stats' ? 'Stats'
                                : 'Map control α'}
                        </button>
                    ))}
                </div>
            </div>

            {/* 3-column body */}
            <div className={styles.viewBody}>
                {/* Left panel */}
                <div className={styles.leftPanelModern}>
                    {loadProgress.total > 0 && loadProgress.loaded < loadProgress.total && (
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>
                                Loading
                                <span className={styles.sectionTitleMeta}>{loadProgress.loaded}/{loadProgress.total}</span>
                            </div>
                            <div className={styles.progressBarModern}>
                                <div className={styles.progressFillModern} style={{ width: `${progressPct}%` }} />
                            </div>
                        </div>
                    )}

                    <div className={styles.section}>
                        <div className={styles.sectionTitle}>View</div>
                        <div className={styles.chipRow}>
                            <button
                                className={`${styles.chip} ${showTeam === 'team' ? styles.chipActive : ''}`}
                                style={{ flex: 1 }}
                                onClick={() => setShowTeam('team')}
                            >{analysisState.teamName || 'Team'}</button>
                            <button
                                className={`${styles.chip} ${showTeam === 'opponent' ? styles.chipActive : ''}`}
                                style={{ flex: 1 }}
                                onClick={() => setShowTeam('opponent')}
                            >{opponentLabel}</button>
                        </div>
                        <div className={styles.chipRow} style={{ marginTop: 6 }}>
                            <button
                                className={`${styles.chip} ${viewSide === 'T' ? styles.chipActive : ''}`}
                                style={{ flex: 1 }}
                                onClick={() => setViewSide('T')}
                            >T</button>
                            <button
                                className={`${styles.chip} ${viewSide === 'CT' ? styles.chipActive : ''}`}
                                style={{ flex: 1 }}
                                onClick={() => setViewSide('CT')}
                            >CT</button>
                        </div>
                        <div className={styles.chipRow} style={{ marginTop: 6 }}>
                            {BUY_TYPES.map(bt => (
                                <button
                                    key={bt.id}
                                    className={`${styles.chip} ${buyFilter.has(bt.id) ? styles.chipActive : ''}`}
                                    style={{ flex: 1 }}
                                    onClick={() => setBuyFilter(prev => {
                                        const next = new Set(prev);
                                        if (next.has(bt.id)) next.delete(bt.id);
                                        else next.add(bt.id);
                                        return next;
                                    })}
                                >{bt.label}</button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.section}>
                        <div className={styles.sectionTitle}>
                            Players
                            {selectedPlayer && (
                                <span
                                    className={styles.sectionTitleMeta}
                                    style={{ cursor: 'pointer', color: '#ff9933' }}
                                    onClick={() => setSelectedPlayer(null)}
                                    title="Clear filter"
                                >Clear</span>
                            )}
                        </div>
                        <div className={styles.playerListModern}>
                            {playerRoster.map(p => (
                                <div
                                    key={p.steamId}
                                    className={`${styles.playerItemModern} ${selectedPlayer === p.steamId ? styles.playerItemActive : ''} ${selectedPlayer && selectedPlayer !== p.steamId ? styles.playerItemDimmed : ''}`}
                                    onClick={() => setSelectedPlayer(prev => prev === p.steamId ? null : p.steamId)}
                                >
                                    <span className={styles.playerDotModern} style={{ background: PLAYER_COLORS[p.colorIdx] }} />
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {playerNames[p.steamId] || p.steamId.slice(-8)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {viewMode === 'heatmap' && (
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>Heatmap</div>
                            <div className={styles.rangeRow}>
                                <span>{fmtClock(heatmapTimeRange[0])}</span>
                                <span style={{ minWidth: 'auto', color: 'rgba(255,255,255,0.45)' }}>to</span>
                                <span>{fmtClock(heatmapTimeRange[1])}</span>
                            </div>
                            <input type="range" className={styles.rangeInput} min={0} max={maxTime} step={1} value={heatmapTimeRange[0]}
                                onChange={e => {
                                    const v = parseFloat(e.target.value);
                                    setHeatmapTimeRange([Math.min(v, heatmapTimeRange[1] - 1), heatmapTimeRange[1]]);
                                }} />
                            <input type="range" className={styles.rangeInput} min={0} max={maxTime} step={1} value={heatmapTimeRange[1]}
                                onChange={e => {
                                    const v = parseFloat(e.target.value);
                                    setHeatmapTimeRange([heatmapTimeRange[0], Math.max(v, heatmapTimeRange[0] + 1)]);
                                }} />
                            <div style={{ marginTop: 4, fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Smoothing</div>
                            <input type="range" className={styles.rangeInput} min={5} max={40} value={heatmapSmoothing}
                                onChange={e => setHeatmapSmoothing(parseInt(e.target.value))} />
                        </div>
                    )}

                    {viewMode === 'grenades' && (
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>Grenades</div>
                            <div className={styles.chipRow}>
                                {(['smoke', 'flash', 'he', 'molotov'] as const).map(type => (
                                    <button
                                        key={type}
                                        className={`${styles.chip} ${grenadeFilter.has(type) ? styles.chipActive : ''}`}
                                        style={{ flex: 1, padding: '6px 8px' }}
                                        onClick={() => {
                                            const next = new Set(grenadeFilter);
                                            if (next.has(type)) next.delete(type); else next.add(type);
                                            setGrenadeFilter(next);
                                        }}
                                    >{type.charAt(0).toUpperCase() + type.slice(1)}</button>
                                ))}
                            </div>
                            <div className={styles.rangeRow} style={{ marginTop: 6 }}>
                                <span>{fmtClock(grenadeTimeRange[0])}</span>
                                <span style={{ minWidth: 'auto', color: 'rgba(255,255,255,0.45)' }}>to</span>
                                <span>{fmtClock(grenadeTimeRange[1])}</span>
                            </div>
                            <input type="range" className={styles.rangeInput} min={0} max={maxTime} step={1} value={grenadeTimeRange[0]}
                                onChange={e => {
                                    const v = parseFloat(e.target.value);
                                    setGrenadeTimeRange([Math.min(v, grenadeTimeRange[1] - 1), grenadeTimeRange[1]]);
                                }} />
                            <input type="range" className={styles.rangeInput} min={0} max={maxTime} step={1} value={grenadeTimeRange[1]}
                                onChange={e => {
                                    const v = parseFloat(e.target.value);
                                    setGrenadeTimeRange([grenadeTimeRange[0], Math.max(v, grenadeTimeRange[0] + 1)]);
                                }} />
                        </div>
                    )}

                    <div className={styles.roundCountFooter}>
                        <span className={styles.roundCountFooterNum}>{visibleRoundKeys.size}</span>
                        rounds visible
                    </div>
                </div>

                {/* Map area */}
                <div className={styles.mapAreaModern}>
                    {viewMode === 'control' && canSeeMapControl ? (
                        <MapControlPanel
                            mapName={analysisState.matches[0]?.mapName ?? ''}
                            teamName={analysisState.teamName}
                            rounds={mapControlRounds}
                            playerNames={playerNames}
                            onOpenRound={(key) => {
                                const replay = loadedRounds.get(key);
                                const roundInfo = analysisState.filteredRounds.find(r => key === `${r.gameId}_r${r.roundNumber}`);
                                const matchInfo = analysisState.matches.find(m => m.gameid === roundInfo?.gameId);
                                if (replay && roundInfo && matchInfo) {
                                    setInspectedRound({ key, replay, round: roundInfo, match: matchInfo });
                                    setOpenedWithControl(true);
                                    setViewMode('default');
                                }
                            }}
                        />
                    ) : viewMode === 'stats' ? (
                        <div style={{ width: '100%', maxWidth: 720, padding: 16 }}>
                            {analysisState.upload ? (
                                // Buy/site win rates come from /Analysis/buysite-winrate, which
                                // only sees league matches — a private upload has no aggregate.
                                <div style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', padding: 32 }}>
                                    Stats er ikke tilgængelig for private demoer — buy/site win-rates bygger på ligakampe.
                                </div>
                            ) : analysisState.filters?.teamId ? (
                                <BuySiteWinRatePanel
                                    teamId={analysisState.filters.teamId}
                                    mapName={analysisState.matches[0]?.mapName}
                                    seasons={analysisState.filters?.seasons ?? []}
                                />
                            ) : (
                                <div style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', padding: 32 }}>
                                    Stats er kun tilgængelig i hold-tilstand. Skift til hold-mode i filteret for at se buy/site win-rates.
                                </div>
                            )}
                        </div>
                    ) : inspectedRound && viewMode === 'default' ? (
                        <AnalysisReplayCanvas
                            replay={inspectedRound.replay}
                            currentFrame={inspectedFrameIdx}
                            mapName={analysisState.matches[0]?.mapName || ''}
                            showMapControl={openedWithControl}
                            playerNames={playerNames}
                            zoom={replayZoom}
                            panOffset={replayPanOffset}
                            onZoomChange={setReplayZoom}
                            onPanOffsetChange={setReplayPanOffset}
                            isPanMode={false}
                        />
                    ) : (
                        <canvas
                            ref={canvasRef}
                            style={{ maxWidth: '100%', maxHeight: '100%', cursor: viewMode === 'grenades' ? 'crosshair' : 'default' }}
                            onClick={handleCanvasClick}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                        />
                    )}

                    {/* Help hint — default-mode interactions. Hidden once a round is
                        inspected or in heatmap/grenades/stats modes (which have their
                        own affordances). */}
                    {viewMode === 'default' && !inspectedRound && (
                        <div className={styles.helpOverlay}>
                            <span className={styles.helpKey}>Click player</span>
                            <span className={styles.helpDesc}>Inspect round</span>
                            <span className={styles.helpKey}>Hover player</span>
                            <span className={styles.helpDesc}>Highlight round</span>
                        </div>
                    )}

                    {/* Floating grenade selection card */}
                    {viewMode === 'grenades' && selectedGrenades.length > 0 && (
                        <div className={styles.grenadeOverlayCard}>
                            <div className={styles.grenadeOverlayHeader}>
                                <span>Selected</span>
                                <span style={{ color: '#ff9933', fontSize: '0.78rem' }}>{selectedGrenades.length}</span>
                            </div>
                            <div className={styles.grenadeOverlayCols}>
                                <span style={{ flex: 1 }}>Player</span>
                                <span style={{ width: 50, textAlign: 'right' }}>Time</span>
                                <span style={{ width: 32, textAlign: 'right' }}>Rnd</span>
                                <span style={{ width: 32, textAlign: 'right' }}>Type</span>
                            </div>

                            {selectedGrenades.map((nade, i) => {
                                const name = playerNames[nade.throwerName] || nade.throwerName.slice(-8);
                                const replay = loadedRounds.get(nade.roundKey);
                                let timeStr = '';
                                if (replay) {
                                    const tickRate = replay.header?.tickRate || 64;
                                    const frameTick = replay.frames[Math.min(nade.frameIdx, replay.frames.length - 1)]?.tick || 0;
                                    const startTick = replay.frames[0]?.tick || 0;
                                    const elapsed = (frameTick - startTick) / tickRate;
                                    const remaining = Math.max(0, 115 - elapsed);
                                    timeStr = `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(Math.floor(remaining % 60)).padStart(2, '0')}`;
                                }
                                return (
                                    <div key={i} className={styles.grenadeOverlayRow}>
                                        <span style={{ flex: 1, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                                        <span style={{ width: 50, textAlign: 'right', color: 'rgba(255,255,255,0.6)', fontFamily: 'Consolas, monospace', fontSize: '0.75rem' }}>{timeStr}</span>
                                        <span style={{ width: 32, textAlign: 'right', color: 'rgba(255,255,255,0.6)' }}>{nade.roundNumber}</span>
                                        <span style={{ width: 32, textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                            {utilIcons[nade.type] && <img src={utilIcons[nade.type]} alt={nade.type} style={{ width: 16, height: 16, filter: 'brightness(0.9)' }} />}
                                        </span>
                                    </div>
                                );
                            })}

                            <div className={styles.grenadeOverlayMeta}>
                                <strong style={{ color: 'var(--text-primary)' }}>{selectedGrenades.length}</strong> throws,{' '}
                                <strong style={{ color: 'var(--text-primary)' }}>{new Set(selectedGrenades.map(g => g.roundKey)).size}</strong> rounds{' '}
                                ({Math.round(selectedGrenades.length / Math.max(1, allGrenades.length) * 100)}%)
                            </div>

                            <button
                                className={styles.grenadeOverlayCta}
                                onClick={() => {
                                    const firstNade = selectedGrenades[0];
                                    if (!firstNade) return;
                                    const roundInfo = analysisState.filteredRounds.find(r => firstNade.roundKey === `${r.gameId}_r${r.roundNumber}`);
                                    const matchInfo = analysisState.matches.find(m => m.gameid === roundInfo?.gameId);
                                    const replay = loadedRounds.get(firstNade.roundKey);
                                    if (roundInfo && matchInfo && replay) {
                                        setInspectedRound({ key: firstNade.roundKey, replay, round: roundInfo, match: matchInfo });
                                        setViewMode('default');
                                    }
                                }}
                            >
                                <PlayArrowIcon style={{ fontSize: 16 }} />
                                Replay all
                            </button>
                        </div>
                    )}

                    {viewMode === 'default' && (
                        <div className={styles.playbackBarModern}>
                            <button className={styles.playbackBarBtn} onClick={() => setIsPlaying(!isPlaying)}>
                                {isPlaying ? (
                                    <svg width="12" height="12" viewBox="0 0 16 16">
                                        <rect x="3" y="2" width="3.5" height="12" rx="1" fill="#fff" />
                                        <rect x="9.5" y="2" width="3.5" height="12" rx="1" fill="#fff" />
                                    </svg>
                                ) : (
                                    <svg width="12" height="12" viewBox="0 0 16 16">
                                        <path d="M4 2.5 L13 8 L4 13.5 Z" fill="#fff" />
                                    </svg>
                                )}
                            </button>
                            <input
                                type="range"
                                className={styles.playbackScrubber}
                                min={0}
                                max={maxTime}
                                step={0.1}
                                value={currentTime}
                                onChange={e => setCurrentTime(parseFloat(e.target.value))}
                            />
                            <span className={styles.playbackTime}>{clockTime}</span>
                            <div className={styles.playbackSpeedGroup}>
                                {[0.5, 1, 2, 4].map(s => (
                                    <button
                                        key={s}
                                        className={`${styles.playbackSpeedBtn} ${speed === s ? styles.playbackSpeedBtnActive : ''}`}
                                        onClick={() => setSpeed(s)}
                                    >
                                        {s}x
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Right panel — inspect or summary */}
                <div className={styles.rightPanelModern}>
                    {inspectedRound && viewMode === 'default' ? (
                        <AnalysisInspectPanel
                            replay={inspectedRound.replay}
                            currentFrame={inspectedFrameIdx}
                            playerNames={playerNames}
                            match={inspectedRound.match}
                            roundNumber={inspectedRound.round.roundNumber}
                            onBack={() => { setInspectedRound(null); setReplayZoom(1); setReplayPanOffset({ x: 0, y: 0 }); }}
                        />
                    ) : summaryStats ? (
                        <>
                            <div className={styles.summaryHeader}>
                                <span className={styles.summaryHeaderTitle}>
                                    <InsightsIcon />
                                    Round Summary
                                </span>
                            </div>

                            <div className={styles.summaryHero}>
                                <span className={styles.summaryHeroNum}>{summaryStats.rounds}</span>
                                <span className={styles.summaryHeroLabel}>
                                    rounds · {summaryStats.matches} {summaryStats.matches === 1 ? 'match' : 'matches'}
                                </span>
                            </div>

                            <div className={styles.summaryGroup}>
                                <div className={styles.summaryGroupTitle}>Side</div>
                                <div className={styles.summarySplit}>
                                    <div className={styles.summarySplitCell}>
                                        <span className={styles.summarySplitLabel}>T</span>
                                        <span className={styles.summarySplitValue}>{summaryStats.tRounds}</span>
                                    </div>
                                    <div className={styles.summarySplitCell}>
                                        <span className={styles.summarySplitLabel}>CT</span>
                                        <span className={styles.summarySplitValue}>{summaryStats.ctRounds}</span>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.summaryGroup}>
                                <div className={styles.summaryGroupTitle}>Outcome</div>
                                <div className={styles.summarySplit}>
                                    <div className={styles.summarySplitCell}>
                                        <span className={styles.summarySplitLabel}>Wins</span>
                                        <span className={styles.summarySplitValue} style={{ color: '#4ade80' }}>{summaryStats.wins}</span>
                                    </div>
                                    <div className={styles.summarySplitCell}>
                                        <span className={styles.summarySplitLabel}>Losses</span>
                                        <span className={styles.summarySplitValue} style={{ color: '#f87171' }}>{summaryStats.losses}</span>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.summaryGroup}>
                                <div className={styles.summaryGroupTitle}>Utility thrown</div>
                                <div className={styles.summaryUtilGrid}>
                                    <div className={styles.summaryUtilCell}>
                                        <img src={utilIcons.smoke} alt="smoke" />
                                        <span className={styles.summaryUtilName}>Smoke</span>
                                        <span className={styles.summaryUtilCount}>{summaryStats.smokes}</span>
                                    </div>
                                    <div className={styles.summaryUtilCell}>
                                        <img src={utilIcons.flash} alt="flash" />
                                        <span className={styles.summaryUtilName}>Flash</span>
                                        <span className={styles.summaryUtilCount}>{summaryStats.flashes}</span>
                                    </div>
                                    <div className={styles.summaryUtilCell}>
                                        <img src={utilIcons.he} alt="he" />
                                        <span className={styles.summaryUtilName}>HE</span>
                                        <span className={styles.summaryUtilCount}>{summaryStats.hes}</span>
                                    </div>
                                    <div className={styles.summaryUtilCell}>
                                        <img src={utilIcons.molotov} alt="molotov" />
                                        <span className={styles.summaryUtilName}>Moly</span>
                                        <span className={styles.summaryUtilCount}>{summaryStats.molys}</span>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.summaryGroup}>
                                <div className={styles.summaryGroupTitle}>Kills</div>
                                <div className={styles.summaryStatRow}>
                                    <span className={styles.summaryStatLabel}>Total {showTeam === 'team' ? '(by team)' : '(by opp.)'}</span>
                                    <span className={styles.summaryStatValue}>{summaryStats.kills}</span>
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default AnalysisViewPage;
